import u from 'umbrellajs';
import { Buffer } from 'buffer';
import videojs from 'video.js';
import type { PlaylistItemContent } from '../playlist';
import type { Client } from '../client';
import env from '../env';

declare global {
    interface Window {
        SVGMapToString: any;
        ReverseSVGMapToString: any;
    }
}

export interface VideoLayer {
    setSource(
        context: string,
        source: PlaylistItemContent | null,
    ): Promise<void>;
    setTemplate(template: string): void;
    setActive(active: boolean, zindex: number, afterFade?: () => void): void;
    isActive(): boolean;
    hasAudio(): boolean;
    getState(): VideoLayerState;
    getStatus(): VideoLayerStatus;
    forceFinish(): void;
}

export type VideoLayerState = 'unknown' | 'playing' | 'finished' | 'error';
type OverlayState = 'unknown' | 'loaded' | 'error';

export type VideoLayerStatus = {
    name: string;
    active: boolean;
    state: VideoLayerState;
    vod: boolean;
    zindex: number;
    source: string;
    stats: any;
};

export class VideoLayerClass {
    id: number;
    name: string = '';
    ref: HTMLElement;
    source: PlaylistItemContent | null = null;
    zindex: number;
    template: string;
    templateCache: Record<string, string> = {};
    overlayState: OverlayState = 'unknown';
    overlayError: string = '';
    mapDetail: string;
    state: VideoLayerState = 'unknown';
    player: any = null;
    active: boolean = false;
    vod: boolean = false;
    finishTimeout: NodeJS.Timeout | null = null;
    client: Client;
    activateBegin: Date = new Date();
    context: string = '';
    onState: (
        id: number,
        name: string,
        src: string,
        state: VideoLayerState,
    ) => void;

    constructor(
        id: number,
        ref: HTMLElement,
        zindex: number,
        overlayTemplate: string,
        mapDetail: string,
        onState: (
            id: number,
            name: string,
            src: string,
            state: VideoLayerState,
        ) => void,
        client: Client,
    ) {
        this.id = id;
        this.ref = ref;
        this.zindex = zindex;
        this.template = overlayTemplate;
        this.mapDetail = mapDetail;
        this.client = client;
        this.onState = onState;

        const layer = u(this.ref);

        this.name = layer.attr('id') || '';

        let overlay = layer.find('.qwertz-overlay');
        let player = layer.find('.qwertz-player');

        let htmloverlay = overlay.first() as HTMLElement;
        htmloverlay.style.zIndex = this.zindex + 1 + '';
        htmloverlay.style.opacity = '0';

        let htmlplayer = player.first() as HTMLElement;
        htmlplayer.style.zIndex = this.zindex + '';
        htmlplayer.style.opacity = '1';
    }

    async sleep(ms: number): Promise<void> {
        return new Promise((resolve) => setTimeout(resolve, ms));
    }

    get10MinStamp(): number {
        const now = new Date();
        const hours = now.getHours(); // z.B. 17
        const minutes = now.getMinutes(); // z.B. 23
        // Rundet die Minuten auf das nächste 10er-Raster ab (0, 10, 20, 30, ...)
        const step10 = Math.floor(minutes / 10) * 10;
        // Erzeugt eine Zahl: HH * 100 + gerundete Minuten -> 17:20 => 1720
        return hours * 100 + step10;
    }

    onOverlayError(message: string): void {
        this.overlayState = 'error';
        this.overlayError = message;
        //this.onState(this.id, this.name, src, 'error');
    }

    async loadTemplate(): Promise<void> {
        this.overlayState = 'unknown';
        this.overlayError = '';

        if (this.source === null) {
            this.onOverlayError('no source available');
            return;
        }

        try {
            let html: string = '';

            const cacheKey = this.get10MinStamp() + this.template;
            if (cacheKey in this.templateCache) {
                html = this.templateCache[cacheKey].slice();
            } else {
                const response = await fetch(
                    `https://livespotting.fra1.cdn.digitaloceanspaces.com/playout/templates/${this.template}/index.html?ts=${cacheKey}`,
                );
                html = await response.text();

                this.templateCache = {};
                this.templateCache[cacheKey] = html.slice();
            }

            // Ersetzt den Platzhalter für die Template ID
            html = html.replace(/{{id}}/g, this.name);
            // Ersetzt den Platzhalter für die Laufzeit der Kamera
            html = html.replace(
                /{{playback_sec}}/g,
                this.source.playback_sec.toString(),
            );
            // Ersetzt den Platzhalter für den Template Namen
            html = html.replace(/{{template_name}}/g, this.template);

            // Ersetzt alle Metadaten-Platzhalter
            if (this.source?.metadata) {
                const metadata = this.source.metadata;

                for (const key in metadata) {
                    const r = new RegExp(`{{${key}}}`, 'g');
                    html = html.replace(r, metadata[key]);
                }

                if (
                    (html.match(/{{map}}/g) !== null ||
                        html.match(/{{mapAsDataURI}}/g) !== null) &&
                    metadata.longitude &&
                    metadata.latitude
                ) {
                    const data = window.ReverseSVGMapToString(
                        this.mapDetail,
                        parseFloat(metadata.latitude),
                        parseFloat(metadata.longitude),
                        {
                            class: 'map',
                            width: 200,
                            height: 200,
                            padding: 15,
                            attributes: {
                                fill: '#fff',
                            },
                            points: [
                                {
                                    center: [
                                        parseFloat(metadata.longitude),
                                        parseFloat(metadata.latitude),
                                    ],
                                    attributes: {
                                        fill: '#d2d2d2',
                                        stroke: '#000',
                                        strokeWidth: '3',
                                        r: '10',
                                    },
                                },
                                {
                                    center: [
                                        parseFloat(metadata.longitude),
                                        parseFloat(metadata.latitude),
                                    ],
                                    attributes: {
                                        fill: '#d2d2d2',
                                        stroke: '#000',
                                        strokeWidth: '3',
                                        r: '6',
                                    },
                                },
                            ],
                        },
                    );
                    html = html.replace(/{{map}}/g, data);
                    html = html.replace(
                        /{{mapAsDataURI}}/g,
                        'data:image/svg+xml;base64,' +
                            Buffer.from(data).toString('base64'),
                    );
                }
            }

            // Suche nach allen Platzhaltern, die nicht ersetzt wurden und löse eine Warnung aus
            let missedPlaceholder: string[] = [];
            for (const x of html.matchAll(/{{.*?}}/g)) {
                missedPlaceholder.push(x[0]);
            }

            if (missedPlaceholder.length !== 0) {
                console.warn(
                    `layer`,
                    this.name,
                    'failed to replace all placeholder in template',
                    missedPlaceholder.join(','),
                );
            }

            // Ersetze alle unersetzten Platzhalter mit leerem String
            for (const placeholder of missedPlaceholder) {
                console.log(this.context, placeholder);
                html = html.replace(placeholder, '');
            }

            let overlay = u(this.ref).find('.qwertz-overlay');
            overlay.html(html);

            // Wir müssen warten, bis alle Bilder geladen sind.
            const images = overlay.find('img');
            const promises: Promise<void>[] = [];

            images.each((node: Element, _: number): void => {
                promises.push(
                    new Promise<void>((resolve, reject) => {
                        u(node).on('error', (event: Event) => {
                            const target =
                                event.target as HTMLImageElement | null;
                            reject(
                                `failed to load image for template "${this.template}": ${
                                    target ? target.src : '[empty img src]'
                                }`,
                            );
                        });
                        u(node).on('load', () => resolve());
                    }),
                );
            });

            try {
                await Promise.all<void>(promises);
            } catch (error) {
                this.onOverlayError(`loading images: ${error}`);
            }

            // Setzt die Fade-In/Out Funktion
            const mainDiv = overlay
                .find(`#${this.name}-main`)
                .first() as HTMLElement | null;
            if (!mainDiv) {
                this.onOverlayError(
                    `mainDiv '#${this.name}-main' not found in template`,
                );
            }

            // Startet ein im Template liegendes <script> sofern es definiert ist.
            const script = overlay.find('script').first() as HTMLElement | null;
            if (script) {
                try {
                    let onShow = (0, eval)(script.innerHTML); // Evaluiere den JavaScript-Code aus dem Template
                    (window as any)[`onShow_${this.name}`] = onShow;
                } catch (error) {
                    this.onOverlayError(`script evaluation: ${error}`);
                    // 'Fehler beim Ausführen des Skripts:'
                }
            } else {
                // 'Kein Skript gefunden'
                (window as any)[`onShow_${this.name}`] = function () {};
            }
        } catch (error) {
            this.onOverlayError(`loading template: ${error}`);
        } finally {
            if (this.overlayState == 'unknown') {
                this.overlayState = 'loaded';
            }
        }
    }

    onPlayerState(src: string, state: VideoLayerState): void {
        if (state == 'error' || state == 'finished') {
            if (this.finishTimeout !== null) {
                clearTimeout(this.finishTimeout);
                this.finishTimeout = null;
            }
        }

        this.state = state;
        this.onState(this.id, this.name, src, state);
    }

    getPlayerOptions(): any {
        const options = {
            id: 'player_' + this.name,
            controls: false,
            autoplay: true,
            preload: 'metadata',
            muted: true,
            fluid: true,
            sources: this.source?.source ? [this.source.source] : [],
        };

        return options;
    }

    loadPlayer(): void {
        this.vod = false;

        if (this.player !== null) {
            this.player.dispose();
            this.player = null;
        }

        u(this.ref)
            .find('.qwertz-player')
            .empty()
            .append(
                `<div class="video-wrapper"><video-js id="${this.name}-video-wrapper"></video-js></div>`,
            );

        console.log(
            this.context,
            this.name,
            'initializing player',
            this.getPlayerOptions(),
        );

        try {
            const player = videojs(
                `${this.name}-video-wrapper`,
                this.getPlayerOptions(),
                () => {
                    console.log(
                        this.context,
                        this.name,
                        'player ready',
                        player.currentSrc(),
                    );

                    player.on('loadedmetadata', async () => {
                        const duration = player.duration();
                        console.log(
                            this.context,
                            this.name,
                            'duration',
                            duration,
                        );
                        if (Number.isFinite(duration)) {
                            this.vod = true;
                            if (!this.active) {
                                player.pause();
                                this.onPlayerState(
                                    player.currentSrc(),
                                    'playing',
                                );
                            }
                        } else {
                            player.play();
                        }

                        console.log(
                            this.context,
                            this.name,
                            `player ready: vod=${this.vod} zindex=${this.zindex}`,
                        );
                    });

                    player.on('playing', () => {
                        // Playing ist playing
                        this.onPlayerState(player.currentSrc(), 'playing');
                    });

                    player.on('pause', () => {
                        // Bei VOD ist Pause das Ende und bei Live ein Fehler
                        if (this.vod) {
                            if (this.active) {
                                this.onPlayerState(
                                    player.currentSrc(),
                                    'finished',
                                );
                            } else {
                                this.onPlayerState(
                                    player.currentSrc(),
                                    'playing',
                                );
                            }
                        } else {
                            this.onPlayerState(player.currentSrc(), 'finished');
                        }
                    });

                    player.tech_.on('retryplaylist', () => {
                        // retryplaylist ist Error
                        this.onPlayerState(player.currentSrc(), 'error');
                    });

                    player.on('error', () => {
                        // error ist Error
                        this.onPlayerState(player.currentSrc(), 'error');
                    });

                    player.play();
                },
            );
            this.player = player;
        } catch (error) {
            // Some errors can't be caught. Chrome may prevent playback because of energy limits.
            this.onPlayerState(`initialize player: ${error}`, 'error');
        }
    }

    async setSource(
        context: string,
        source: PlaylistItemContent | null,
    ): Promise<void> {
        this.context = context;
        this.source = source;
        this.state = 'unknown';

        await this.sleep(1000);

        if (this.source?.type !== 'external_url') {
            await this.loadTemplate();
            if (this.overlayState != 'loaded') {
                this.onPlayerState(this.overlayError, 'error');
            }
        } else {
            let overlay = u(this.ref).find('.qwertz-overlay');
            overlay.html('');
        }

        if (this.state == 'unknown') {
            // If state is still unknown, then loading the template didn't cause an error.
            this.loadPlayer();
        }
    }

    setTemplate(template: string): void {
        this.template = template;
    }

    setActive(active: boolean, zindex: number, afterFade?: () => void): void {
        const wasActive = this.active;
        this.active = active;

        // Prevent activating a layer more than once.
        if (wasActive && wasActive == active) {
            return;
        }

        this.zindex = zindex;

        const layer = u(this.ref);
        const overlay = layer.find('.qwertz-overlay');
        const player = layer.find('.qwertz-player');

        let htmloverlay = overlay.first() as HTMLElement;
        htmloverlay.style.zIndex = this.zindex + 1 + '';
        htmloverlay.style.opacity = '0';

        let htmlplayer = player.first() as HTMLElement;
        htmlplayer.style.zIndex = this.zindex + '';
        htmlplayer.style.opacity = '1';

        if (this.finishTimeout !== null) {
            console.log(this.context, this.name, 'unsetting timeout');
            clearTimeout(this.finishTimeout);
            this.finishTimeout = null;
        }

        if (!wasActive && this.active) {
            console.log(this.context, this.name, 'activating layer');

            this.activateBegin = new Date();
            this.client.sendState({ position: this.source?.index });

            console.log(
                this.context,
                this.name,
                `is visible from ${this.activateBegin.toLocaleTimeString()} for`,
                this.source ? this.source.playback_sec : 5,
                `seconds`,
            );

            if (this.vod) {
                this.player.currentTime(0);
            }

            if (this.source?.enable_audio) {
                this.player.muted(false);
                this.player.volume(1);
            }
            this.player.play();

            const onShowFunction = (window as any)[`onShow_${this.name}`];
            if (typeof onShowFunction === 'function') {
                onShowFunction(this.name, true);
            }

            const performFadeIn = (opacity: number) => {
                opacity = Math.min(opacity + 0.1, 1);

                htmloverlay.style.opacity = opacity + '';

                if (opacity < 1) {
                    setTimeout(performFadeIn, 50, opacity);
                } else {
                    if (afterFade) {
                        afterFade();
                    }
                }
            };

            // Startet den Fade-In Effekt
            performFadeIn(0);

            if (Math.random() > 1 - env.chaosContentFailEarly) {
                this.finishTimeout = setTimeout(
                    () => {
                        this.onPlayerState('chaos: fail early', 'error');
                    },
                    (this.source ? this.source.playback_sec * 1000 : 5000) *
                        Math.random(),
                );
            } else {
                this.finishTimeout = setTimeout(
                    () => {
                        this.onPlayerState('timeout reached', 'finished');
                    },
                    this.source ? this.source.playback_sec * 1000 : 5000,
                );
            }
        } else if (wasActive && !this.active) {
            console.log(this.context, this.name, 'de-activating layer');

            this.player.volume(0);
            this.player.muted(true);

            // Berechnet die Zeit, in der das Video tatsächlich zu sehen war
            // > Hier kommt später der API-Call rein
            const endTime = new Date();
            const duration =
                (endTime.getTime() - this.activateBegin.getTime()) / 1000;

            this.client.sendTracking({
                content_id: this.source?.id,
                content_type: this.source?.type,
                start_date: this.activateBegin.toISOString(),
                end_date: endTime.toISOString(),
            });

            console.log(
                this.context,
                this.name,
                `was visible from ${this.activateBegin.toLocaleTimeString()} until ${endTime.toLocaleTimeString()} for ${duration}s`,
            );

            const performFadeOut = (opacity: number) => {
                opacity = Math.max(opacity - 0.1, 0);

                htmlplayer.style.opacity = opacity + '';

                if (opacity > 0) {
                    setTimeout(performFadeOut, 25, opacity);
                } else {
                    this.player.dispose();
                    this.player = null;
                    if (afterFade) {
                        afterFade();
                    }
                }
            };

            // Startet den Fade-Out Effekt
            performFadeOut(1);

            const onShowFunction = (window as any)[`onShow_${this.name}`];
            if (typeof onShowFunction === 'function') {
                onShowFunction(this.name, false);
            }
        }
    }

    getState(): VideoLayerState {
        return this.state;
    }

    isActive(): boolean {
        return this.active;
    }

    hasAudio(): boolean {
        if (!this.source) {
            return false;
        }

        return this.source.enable_audio;
    }

    getStatus(): VideoLayerStatus {
        let status: VideoLayerStatus = {
            name: this.name,
            active: this.active,
            state: this.state,
            vod: this.vod,
            zindex: this.zindex,
            source: this.source?.source.src || '',
            stats: null,
        };

        return status;
    }

    forceFinish(): void {
        setTimeout(() => {
            this.onPlayerState('forced finish', 'finished');
        }, 100);
    }
}
